import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check, X, ArrowLeft, Radar, ExternalLink, Wallet, Plane, Building2, Luggage, Car,
  Clock, Sparkles, ShieldCheck, AlertTriangle, Loader2, Share2, CalendarRange,
} from "lucide-react";
import { Shell } from "../components/Shell";
import { api, eur } from "../lib/api";
import { goalBudget, loadGoal, tripDateContext } from "../lib/goal";
import { saveSelectedTrip, loadSelectedTrip, saveLiveWatch } from "../lib/selectedTrip";
import { useI18n, badgeKey, verdictKey } from "../i18n";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export default function Decision() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [trip, setTrip] = useState(null);
  const [take, setTake] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);
  const [watching, setWatching] = useState(false);
  const budget = goalBudget();
  const goal = loadGoal();
  const isSelectedLive = id === "selected";
  const dateContext = tripDateContext(isSelectedLive ? trip : goal, lang);

  const COMP_META = {
    flights: { icon: Plane, label: t("decision.cFlights") },
    hotel: { icon: Building2, label: t("decision.cHotel", { n: trip?.nights || goal?.nights || 0 }) },
    bags: { icon: Luggage, label: t("decision.cBags") },
    transfers: { icon: Car, label: t("decision.cTransfers") },
  };

  useEffect(() => {
    setTrip(null); setTake(null);
    if (id === "selected") {
      const selected = loadSelectedTrip();
      if (!selected || selected.source === "controlled") {
        toast.error(t("decision.notFound"));
        return;
      }
      setTrip(selected);
      setTake(t("decision.liveTake"));
      return;
    }
    api.trip(id, budget).then((x) => { setTrip(x); saveSelectedTrip(x, goal || {}); }).catch(() => toast.error(t("decision.notFound")));
    api.aiTake(id, budget).then((r) => setTake(r.take)).catch(() => {});
  }, [id, budget, lang]); // eslint-disable-line

  const watch = async () => {
    setWatching(true);
    try {
      if (isSelectedLive) {
        saveLiveWatch(trip);
      } else {
        await api.addWatch(id);
      }
      toast.success(t("decision.watching"), { description: t("decision.watchingDesc") });
      navigate("/watch");
    } catch { toast.error(t("decision.watchErr")); } finally { setWatching(false); }
  };

  const share = async () => {
    if (!trip) return;
    const bl = trip.budget_left >= 0 ? t("common.belowBudget", { x: eur(trip.budget_left) }) : t("common.overBudget", { x: eur(-trip.budget_left) });
    const summary = `Decidova · ${trip.destination} · ${t("common.total")} ${eur(trip.total)} · ${t(verdictKey(trip.decision))} · ${bl}\n` +
      trip.reasons.slice(0, 3).map((r) => "• " + r).join("\n") +
      `\n— Decidova: ${t("landing.heroPre")}${t("landing.heroAccent")}`;
    try {
      if (navigator.share) { await navigator.share({ title: "Decidova", text: summary }); }
      else { await navigator.clipboard.writeText(summary); toast.success(t("common.copied")); }
    } catch (e) { try { await navigator.clipboard.writeText(summary); toast.success(t("common.copied")); } catch {} }
  };

  if (!trip) return <Shell active="plan"><div className="dv-skeleton h-[520px] rounded-2xl" /></Shell>;

  const isBook = trip.decision === "BOOK";
  const c = isBook ? { main: "var(--book)", ink: "var(--book-ink)", soft: "var(--book-soft)" } : { main: "var(--wait)", ink: "var(--wait-ink)", soft: "var(--wait-soft)" };
  const bk = badgeKey(trip.badge);
  const weeksNote = isSelectedLive
    ? (trip.accommodation_source === "live" ? t("decision.liveCompleteNote") : t("decision.livePartialNote"))
    : (trip.compatible_windows === 1 ? t("decision.estimatedNote1") : t("decision.estimatedNote", { w: trip.compatible_windows }));

  return (
    <Shell active="plan">
      <button onClick={() => navigate("/results")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 mb-4 hover:text-slate-900 transition-colors"><ArrowLeft size={16} /> {t("decision.back")}</button>

      <motion.div key={trip.decision + lang} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative overflow-hidden dv-card mb-5" data-testid="decision-hero" style={{ borderColor: c.soft }}>
        <div className="relative h-52 sm:h-64" style={!trip.image ? { background: "linear-gradient(135deg, var(--ink), var(--brand))" } : undefined}>
          {trip.image && <img src={trip.image} alt={trip.destination} className="w-full h-full object-cover" />}
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(15,23,42,0.15), rgba(15,23,42,0.72))" }} />
          <div className="absolute top-4 left-4 dv-pill text-[11px] px-3 py-1.5 bg-white/90 text-slate-800 font-display uppercase tracking-wide">
            {bk ? t(bk) : trip.match_score != null ? `${trip.match_score}${t("results.matchSuffix")}` : t("results.liveBadge")}
          </div>
          <div className="absolute bottom-4 left-5 right-5 text-white">
            {(trip.region || trip.country) && <div className="text-xs opacity-90">{[trip.region, trip.country].filter(Boolean).join(" · ")}</div>}
            <div className="font-display font-extrabold text-3xl leading-none">{trip.destination}</div>
          </div>
        </div>

        <div className="p-5 sm:p-7">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[11px] font-semibold tracking-[0.24em] font-display" style={{ color: c.ink }}>{t("decision.recommends")}</div>
              <motion.div initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }}
                className="font-display font-extrabold text-5xl sm:text-6xl tracking-tight mt-1" style={{ color: c.main }} data-testid="decision-verdict">
                {t(verdictKey(trip.decision))}
              </motion.div>
              {dateContext && (
                <div className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700" data-testid="decision-date-context">
                  <CalendarRange size={15} color="var(--brand)" /> {dateContext.label}
                </div>
              )}
              <div className="mt-2 flex items-center gap-2 text-sm font-medium" style={{ color: c.ink }}>
                {isBook ? <ShieldCheck size={16} /> : <Clock size={16} />}
                {isBook ? t("decision.strongTime") : t("decision.worthWatching")}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-semibold tracking-[0.22em] text-slate-400 font-display">{t("common.total")}</div>
              <div className="dv-total text-4xl sm:text-5xl text-slate-900" data-testid="decision-total">{eur(trip.total)}</div>
              <div className="text-xs mt-1" style={{ color: trip.budget_left >= 0 ? "var(--book-ink)" : "var(--alert)" }}>
                {trip.budget_left >= 0 ? t("common.belowBudget", { x: eur(trip.budget_left) }) : t("common.overBudget", { x: eur(-trip.budget_left) })}
              </div>
            </div>
          </div>

          <div className="mt-5 rounded-2xl p-4 flex gap-3" style={{ background: c.soft }}>
            <Sparkles size={17} color={c.ink} className="shrink-0 mt-0.5" />
            <div>
              <div className="text-[11px] font-semibold" style={{ color: c.ink }}>{t("decision.takeLabel")}</div>
              <AnimatePresence mode="wait">
                {take ? (
                  <motion.p key="t" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-sm text-slate-700 leading-relaxed mt-0.5" data-testid="ai-take">{take}</motion.p>
                ) : (
                  <div className="mt-1.5 space-y-1.5"><div className="dv-skeleton h-3 w-full rounded" /><div className="dv-skeleton h-3 w-3/4 rounded" /></div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.div>

      <div className="grid md:grid-cols-[1.1fr_0.9fr] gap-5">
        <div className="dv-card p-5 sm:p-6">
          <div className="font-display font-bold text-lg mb-4">{isBook ? t("decision.whyBook") : t("decision.whyWait")}</div>
          <ul className="space-y-3" data-testid="decision-reasons">
            {trip.reasons.map((r, i) => (
              <motion.li key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.07 }} className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: isBook ? "var(--book-soft)" : "var(--wait-soft)" }}>
                  {isBook ? <Check size={12} color="var(--book-ink)" /> : <AlertTriangle size={11} color="var(--wait-ink)" />}
                </span>
                <span className="text-sm text-slate-700 leading-relaxed">{r}</span>
              </motion.li>
            ))}
          </ul>
        </div>

        <div className="dv-card p-5 sm:p-6">
          <div className="font-display font-bold text-lg mb-4">{t("decision.whatsInTotal")}</div>
          {Object.entries(trip.components).map(([k, v]) => {
            const M = COMP_META[k]; if (!M) return null;
            return (
              <div key={k} className="flex items-center justify-between py-2.5 border-b border-[var(--line)]">
                <span className="inline-flex items-center gap-2 text-sm text-slate-600"><M.icon size={15} color="var(--muted)" /> {M.label}</span>
                <span className="font-mono-num text-sm font-semibold text-slate-800">{v === 0 ? "—" : eur(v)}</span>
              </div>
            );
          })}
          <div className="flex items-center justify-between pt-3.5">
            <span className="text-[11px] font-semibold tracking-[0.2em] text-slate-400 font-display">{t("common.total")}</span>
            <span className="dv-total text-2xl text-slate-900">{eur(trip.total)}</span>
          </div>
          <div className="mt-3 flex items-center gap-2 rounded-xl px-3 py-2" style={{ background: "var(--paper)" }}>
            <Wallet size={14} color="var(--muted)" />
            <span className="text-xs text-slate-500">{weeksNote}</span>
          </div>
        </div>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-3">
        <button onClick={() => setBookOpen(true)} data-testid="book-externally-button" className={`dv-btn ${isBook ? "dv-btn-book" : "dv-btn-primary"} w-full text-base py-4`}>
          <ExternalLink size={17} /> {t("decision.bookExternally")}
        </button>
        <button onClick={watch} disabled={watching} data-testid="watch-trip-button" className="dv-btn dv-btn-ghost w-full text-base py-4">
          {watching ? <Loader2 size={17} className="animate-spin" /> : <Radar size={17} color="var(--brand)" />} {t("decision.watchTrip")}
        </button>
      </div>
      <button onClick={share} data-testid="share-decision-button" className="dv-btn dv-btn-ghost w-full text-sm py-3 mt-3"><Share2 size={16} color="var(--brand)" /> {t("common.share")}</button>
      <p className="mt-4 text-center text-sm text-slate-500">{t("common.recommendsControl")}</p>

      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent data-testid="book-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><ExternalLink size={18} color="var(--brand)" /> {t("decision.dialogTitle")}</DialogTitle>
            <DialogDescription className="pt-2 leading-relaxed">{t("decision.dialogDesc", { d: trip.destination })}</DialogDescription>
          </DialogHeader>
          <div className="rounded-xl p-4 my-1" style={{ background: "var(--paper)" }}>
            <div className="flex justify-between text-sm"><span className="text-slate-500">{t("decision.dialogTrip", { d: trip.destination, n: trip.nights || goal?.nights || 0 })}</span><span className="dv-total text-slate-900">{eur(trip.total)}</span></div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setBookOpen(false)} className="dv-btn dv-btn-ghost px-4 py-2.5 text-sm">{t("decision.stayInDecidova")}</button>
            <button onClick={() => { if (!isSelectedLive) saveSelectedTrip(trip, goal || {}); setBookOpen(false); toast.success(t("decision.handoff"), { description: t("decision.handoffDesc") }); navigate("/journey"); }} data-testid="book-confirm-button" className="dv-btn dv-btn-primary px-4 py-2.5 text-sm">
              {t("decision.continuePartner")} <ExternalLink size={15} />
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
